import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Users, Search, Plus, Download, Upload, MoreHorizontal, Mail, Eye, Trash2, SlidersHorizontal,
  AlertCircle,
} from 'lucide-react';
import { EmployeeImportDialog } from './EmployeeImportDialog';

import { useEmployeeStore } from '../../../store/employeeStore';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Badge } from '../../../components/Badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/Avatar';
import { DataTable, type Column } from '../../../components/DataTable';
import { Pagination } from '../../../components/Pagination';
import { EmptyState } from '../../../components/EmptyState';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../components/DropdownMenu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
  exportRows, isoDate, EXPORT_FORMAT_LABELS, type ExportColumn, type ExportFormat,
} from '../../../lib/export';
import toast from 'react-hot-toast';

type StatusKey = 'active' | 'on-leave' | 'terminated' | 'resigned';

const statusMeta: Record<string, { label: string; variant: any }> = {
  active: { label: 'Active', variant: 'success' },
  'on-leave': { label: 'On leave', variant: 'warning' },
  terminated: { label: 'Terminated', variant: 'destructive' },
  resigned: { label: 'Resigned', variant: 'muted' },
};

const PAGE_SIZE = 10;

function initials(first?: string, last?: string, display?: string) {
  const f = first?.[0] ?? display?.[0] ?? '?';
  const l = last?.[0] ?? '';
  return (f + l).toUpperCase();
}

function fmtDate(value?: string) {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'dd MMM yyyy');
  } catch {
    return value;
  }
}

type EmployeeRow = ReturnType<typeof useEmployeeStore.getState>['employees'][number];

/**
 * Columns for the exported file.
 *
 * Intentionally wider than the on-screen table: the grid is trimmed to what
 * fits, but an export is what people load into a spreadsheet to actually work
 * with, so it carries phone, location and manager too. Status is exported as
 * the human label rather than the raw enum.
 */
const exportColumns: ExportColumn<EmployeeRow>[] = [
  { header: 'Employee ID', value: (e) => e.employeeCode },
  { header: 'First Name', value: (e) => e.firstName },
  { header: 'Last Name', value: (e) => e.lastName },
  { header: 'Email', value: (e) => e.email },
  { header: 'Phone', value: (e) => e.phone },
  { header: 'Department', value: (e) => e.department },
  { header: 'Designation', value: (e) => e.designation },
  { header: 'Status', value: (e) => statusMeta[e.status]?.label ?? e.status },
  { header: 'Join Date', value: (e) => isoDate(e.joinDate) },
  { header: 'Location', value: (e) => e.location },
  { header: 'Manager', value: (e) => e.managerName },
];

export function EmployeeList() {
  const navigate = useNavigate();
  const { employees, fetchEmployees, fetchAllEmployees, deleteEmployee, isLoading } = useEmployeeStore();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeRow | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEmployee(deleteTarget.id);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
      toast.success('Employee removed');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to remove employee');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteBulk = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(ids.map((id) => deleteEmployee(id)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    setDeleting(false);
    setBulkDeleteOpen(false);
    setSelected(new Set());
    if (failed === 0) toast.success(`${ids.length} employee${ids.length === 1 ? '' : 's'} removed`);
    else toast.error(`${failed} of ${ids.length} could not be removed`);
  };

  useEffect(() => {
    // Load the whole roster (up to the server's 200 max) so search, status
    // filter and pagination work across everyone — not just the first page.
    // The default fetch returns 20 rows, which capped the list at 20.
    fetchEmployees({ limit: 200 });
  }, [fetchEmployees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      const matchesQuery =
        !q ||
        `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLowerCase().includes(q) ||
        (e.email?.toLowerCase() ?? '').includes(q) ||
        (e.department?.toLowerCase() ?? '').includes(q) ||
        (e.designation?.toLowerCase() ?? '').includes(q) ||
        (e.employeeCode?.toLowerCase() ?? '').includes(q);
      const matchesStatus = status === 'all' || e.status === status;
      return matchesQuery && matchesStatus;
    });
  }, [employees, search, status]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = page > pageCount ? 1 : page;
  const pageRows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const resetPage = () => setPage(1);

  /**
   * Export what the user is currently looking at.
   *
   * Scope, in order of precedence:
   *   1. an explicit row selection, if there is one;
   *   2. otherwise every employee matching the active search and status filter.
   *
   * Case 2 re-fetches from the server rather than exporting the `employees`
   * already in the store. The list endpoint returns 20 rows per page, so the
   * store usually holds only the first page — exporting that would produce a
   * file that looks complete but silently stops at 20 rows.
   */
  const handleExport = async (format: ExportFormat) => {
    if (exporting) return;
    setExporting(format);
    setExportError(null);

    try {
      let rows: EmployeeRow[];

      if (selected.size > 0) {
        // A selection can only contain rows already loaded, so use them
        // directly and keep the on-screen order.
        rows = filtered.filter((e) => selected.has(e.id));
      } else {
        const params: Record<string, string> = {};
        if (search.trim()) params.search = search.trim();
        if (status !== 'all') params.status = status;
        rows = await fetchAllEmployees(params);

        // The server matches `search` its own way, so re-apply the client-side
        // predicate. Otherwise the file can disagree with the visible table.
        const q = search.trim().toLowerCase();
        if (q) {
          rows = rows.filter(
            (e) =>
              `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLowerCase().includes(q) ||
              (e.email?.toLowerCase() ?? '').includes(q) ||
              (e.department?.toLowerCase() ?? '').includes(q) ||
              (e.designation?.toLowerCase() ?? '').includes(q) ||
              (e.employeeCode?.toLowerCase() ?? '').includes(q)
          );
        }
        if (status !== 'all') rows = rows.filter((e) => e.status === status);
      }

      if (rows.length === 0) {
        setExportError('Nothing to export — no employees match the current filters.');
        return;
      }

      exportRows(rows, exportColumns, format, 'employees');
    } catch (err: any) {
      setExportError(
        err?.response?.data?.message || err?.message || 'Export failed. Please try again.'
      );
    } finally {
      setExporting(null);
    }
  };

  const columns: Column<(typeof employees)[number]>[] = [
    {
      id: 'employee',
      header: 'Employee',
      sortable: true,
      sortAccessor: (e) => `${e.firstName} ${e.lastName}`.toLowerCase(),
      cell: (e) => (
        <div className="flex items-center gap-3">
          <Avatar size="sm">
            {e.avatar && <AvatarImage src={e.avatar} alt={e.displayName} />}
            <AvatarFallback>{initials(e.firstName, e.lastName, e.displayName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">
              {e.firstName} {e.lastName}
            </p>
            <p className="truncate text-xs text-muted-foreground">{e.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'code',
      header: 'ID',
      sortable: true,
      sortAccessor: (e) => e.employeeCode,
      cell: (e) => (
        <span className="font-mono text-xs text-muted-foreground">
          {e.employeeCode || '—'}
        </span>
      ),
    },
    {
      id: 'department',
      header: 'Department',
      sortable: true,
      sortAccessor: (e) => e.department ?? '',
      cell: (e) => <span className="text-sm">{e.department || '—'}</span>,
    },
    {
      id: 'designation',
      header: 'Designation',
      cell: (e) => (
        <span className="text-sm text-muted-foreground">{e.designation || '—'}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortAccessor: (e) => e.status,
      cell: (e) => {
        const meta = statusMeta[e.status] ?? { label: e.status, variant: 'muted' };
        return (
          <Badge variant={meta.variant} dot>
            {meta.label}
          </Badge>
        );
      },
    },
    {
      id: 'joinDate',
      header: 'Joined',
      align: 'right',
      sortable: true,
      sortAccessor: (e) => e.joinDate ?? '',
      cell: (e) => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {fmtDate(e.joinDate)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '48px',
      cell: (e) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(ev) => ev.stopPropagation()}>
            <Button variant="ghost" size="icon-sm" aria-label="Row actions">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(ev) => ev.stopPropagation()}>
            <DropdownMenuItem onClick={() => navigate(`/people/employees/${e.id}`)}>
              <Eye className="size-4" /> View profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = `mailto:${e.email}`)}>
              <Mail className="size-4" /> Email
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onClick={() => setDeleteTarget(e)}>
              <Trash2 className="size-4" /> Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const toolbar = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 items-center gap-2">
        <div className="w-full sm:max-w-xs">
          <Input
            leftIcon={<Search />}
            placeholder="Search employees…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); resetPage(); }}>
          <SelectTrigger className="w-[150px]">
            <SlidersHorizontal className="size-4 text-muted-foreground" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="on-leave">On leave</SelectItem>
            <SelectItem value="terminated">Terminated</SelectItem>
            <SelectItem value="resigned">Resigned</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        {selected.size > 0 && (
          <>
            <span className="text-sm text-muted-foreground">{selected.size} selected</span>
            <Button variant="outline" size="sm">
              <Mail /> Email
            </Button>
            <Button variant="outline" size="sm" className="text-destructive" onClick={() => setBulkDeleteOpen(true)}>
              <Trash2 /> Remove
            </Button>
          </>
        )}

        {/* Export stays available with a selection active — exporting the rows
            you just picked is the common case, not a rarer one. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              isLoading={exporting !== null}
              disabled={exporting !== null}
            >
              {exporting === null && <Download />}
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {selected.size > 0
                ? `${selected.size} selected row${selected.size === 1 ? '' : 's'}`
                : status === 'all' && !search.trim()
                  ? 'All employees'
                  : 'All matching the current filters'}
            </div>
            <DropdownMenuSeparator />
            {(Object.keys(EXPORT_FORMAT_LABELS) as ExportFormat[]).map((fmt) => (
              <DropdownMenuItem key={fmt} onClick={() => handleExport(fmt)}>
                <Download className="size-4" /> {EXPORT_FORMAT_LABELS[fmt]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
          <Upload /> Import
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Employees"
        description={`${employees.length} team member${employees.length === 1 ? '' : 's'} across your organization`}
        actions={
          <Button onClick={() => navigate('/people/employees/new')}>
            <Plus /> Add employee
          </Button>
        }
      />

      {/* There is no toast system in the app, so export failures are reported
          inline rather than being swallowed into the console. */}
      {exportError && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span className="flex-1">{exportError}</span>
          <button
            type="button"
            onClick={() => setExportError(null)}
            className="shrink-0 text-xs underline underline-offset-2"
          >
            Dismiss
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={pageRows}
        keyExtractor={(e) => e.id}
        isLoading={isLoading}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        onRowClick={(e) => navigate(`/people/employees/${e.id}`)}
        toolbar={toolbar}
        footer={
          filtered.length > 0 ? (
            <Pagination
              page={current}
              pageCount={pageCount}
              onPageChange={setPage}
              total={filtered.length}
              pageSize={PAGE_SIZE}
            />
          ) : undefined
        }
        empty={
          <EmptyState
            icon={<Users />}
            title={search || status !== 'all' ? 'No matching employees' : 'No employees yet'}
            description={
              search || status !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Add your first team member to get started.'
            }
            action={
              !search && status === 'all' ? (
                <Button onClick={() => navigate('/people/employees/new')}>
                  <Plus /> Add employee
                </Button>
              ) : undefined
            }
          />
        }
      />

      {/* Single delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Remove employee</DialogTitle>
            <DialogDescription>
              Remove <span className="font-medium text-foreground">{deleteTarget?.firstName} {deleteTarget?.lastName}</span> from the directory? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteOne} isLoading={deleting}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Remove {selected.size} employee{selected.size === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>
              Remove the selected employee{selected.size === 1 ? '' : 's'} from the directory? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setBulkDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteBulk} isLoading={deleting}>Remove {selected.size}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmployeeImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => fetchEmployees({ limit: 200 })} />
    </div>
  );
}
